import { useEffect, useRef, useState } from 'react'
import {
  Box, Button, Card, CardHeader, CardBody, Heading, Text, VStack, HStack,
  FormControl, FormLabel, Input, Select, Textarea, Table, Thead, Tbody, Tr, Th, Td, Badge,
  Step, StepIndicator, StepStatus, StepIcon, StepNumber, StepTitle, StepDescription,
  Stepper, useSteps, Tag, TagLabel, TagCloseButton, Wrap, WrapItem,
} from '@chakra-ui/react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { ContentPushService, type ContentPushTargetScope } from '@/lib/services/contentPushService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'

type DeliveryRow = {
  id: number
  content_package_id: number
  device_id: number
  status: string
  delivered_at?: string | null
  acknowledged_at?: string | null
}

const steps = [{ title: 'Compose' }, { title: 'Review' }, { title: 'Deliveries' }]

function TargetIdsInput({
  scope,
  value,
  onChange,
}: {
  scope: ContentPushTargetScope
  value: string[]
  onChange: (ids: string[]) => void
}) {
  const [inputVal, setInputVal] = useState('')

  if (scope === 'all') return null

  const label = scope === 'user' ? 'User IDs' : scope === 'cluster' ? 'Cluster IDs / Codes' : 'Region IDs / Codes'
  const placeholder = scope === 'user' ? 'e.g. 101, 202' : 'e.g. NCR, R03'

  const addEntry = (raw: string) => {
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean)
    const next = Array.from(new Set([...value, ...parts]))
    onChange(next)
    setInputVal('')
  }

  const remove = (tag: string) => onChange(value.filter((v) => v !== tag))

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <HStack mb={2}>
        <Input
          value={inputVal}
          placeholder={placeholder}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              if (inputVal.trim()) addEntry(inputVal)
            }
          }}
        />
        <Button size="sm" onClick={() => { if (inputVal.trim()) addEntry(inputVal) }} flexShrink={0}>Add</Button>
      </HStack>
      {value.length > 0 && (
        <Wrap spacing={2}>
          {value.map((tag) => (
            <WrapItem key={tag}>
              <Tag size="sm" colorScheme="blue" borderRadius="full">
                <TagLabel>{tag}</TagLabel>
                <TagCloseButton onClick={() => remove(tag)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
      <Text fontSize="xs" color="gray.500" mt={1}>Press Enter or comma to add. Click × to remove.</Text>
    </FormControl>
  )
}

export default function ContentPushPage() {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const { activeStep, goToNext, goToPrevious, setActiveStep } = useSteps({ index: 0, count: steps.length })
  const [title, setTitle] = useState('')
  const [type, setType] = useState('announcement')
  const [payload, setPayload] = useState('{}')
  const [targetScope, setTargetScope] = useState<ContentPushTargetScope>('all')
  const [targetIds, setTargetIds] = useState<string[]>([])
  const [publishing, setPublishing] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([])
  const retryBtnRef = useRef<HTMLButtonElement>(null)

  const loadDeliveries = () => {
    ContentPushService.listDeliveries({ per_page: 50 }).then((res) => {
      setDeliveries(res?.data ?? [])
    }).catch(() => {})
  }

  useEffect(() => { loadDeliveries() }, [])

  const handleScopeChange = (scope: ContentPushTargetScope) => {
    setTargetScope(scope)
    setTargetIds([])
  }

  const handlePublish = async () => {
    setPublishing(true)
    try {
      let parsed: Record<string, unknown> = {}
      try { parsed = JSON.parse(payload) } catch { throw new Error('Payload must be valid JSON') }
      await ContentPushService.publish({
        title,
        type,
        payload: parsed,
        target_scope: targetScope,
        ...(targetScope !== 'all' && targetIds.length > 0 ? { target_ids: targetIds } : {}),
      })
      showSuccess('Success', 'Content package published')
      setActiveStep(2)
      loadDeliveries()
    } catch (e) {
      showError(e)
    } finally {
      setPublishing(false)
    }
  }

  const handleRetryFailed = async () => {
    setRetrying(true)
    try {
      await ContentPushService.retryFailed()
      showSuccess('Success', 'Retry triggered for failed deliveries')
      loadDeliveries()
    } catch (e) {
      showError(e)
    } finally {
      setRetrying(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4}>
        <Card mb={4}>
          <CardHeader>
            <Heading size="md">Content Push Console</Heading>
            <Text fontSize="sm" color={colors.text}>Publish mobile content packages and track delivery</Text>
          </CardHeader>
          <CardBody>
            <Stepper index={activeStep} mb={6} size="sm">
              {steps.map((step) => (
                <Step key={step.title}>
                  <StepIndicator><StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} /></StepIndicator>
                  <Box flexShrink={0}><StepTitle>{step.title}</StepTitle><StepDescription>{step.title}</StepDescription></Box>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <VStack align="stretch" spacing={4}>
                <FormControl isRequired><FormLabel>Title</FormLabel><Input value={title} onChange={(e) => setTitle(e.target.value)} /></FormControl>
                <FormControl><FormLabel>Type</FormLabel>
                  <Select value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="announcement">Announcement</option>
                    <option value="checklist">Checklist</option>
                    <option value="config">Config</option>
                    <option value="catalog_delta">Catalog Delta</option>
                  </Select>
                </FormControl>
                <FormControl><FormLabel>Target Scope</FormLabel>
                  <Select value={targetScope} onChange={(e) => handleScopeChange(e.target.value as ContentPushTargetScope)}>
                    <option value="all">All devices</option>
                    <option value="cluster">Cluster</option>
                    <option value="region">Region</option>
                    <option value="user">User</option>
                  </Select>
                </FormControl>
                <TargetIdsInput scope={targetScope} value={targetIds} onChange={setTargetIds} />
                <FormControl><FormLabel>Payload (JSON)</FormLabel><Textarea rows={6} value={payload} onChange={(e) => setPayload(e.target.value)} fontFamily="mono" /></FormControl>
                <HStack><Button colorScheme="blue" onClick={goToNext}>Next</Button></HStack>
              </VStack>
            )}

            {activeStep === 1 && (
              <VStack align="stretch" spacing={3}>
                <Text><strong>Title:</strong> {title}</Text>
                <Text><strong>Type:</strong> {type}</Text>
                <Text><strong>Scope:</strong> {targetScope}</Text>
                {targetScope !== 'all' && targetIds.length > 0 && (
                  <Box>
                    <Text as="span"><strong>Target IDs: </strong></Text>
                    <Wrap display="inline-flex" spacing={1} ml={1}>
                      {targetIds.map((id) => (
                        <WrapItem key={id}><Tag size="sm" colorScheme="blue"><TagLabel>{id}</TagLabel></Tag></WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
                <Box bg="gray.50" p={3} borderRadius="md" fontFamily="mono" fontSize="sm" whiteSpace="pre-wrap">{payload}</Box>
                <HStack>
                  <Button variant="ghost" onClick={goToPrevious}>Back</Button>
                  <Button colorScheme="blue" isLoading={publishing} onClick={handlePublish}>Publish</Button>
                </HStack>
              </VStack>
            )}

            {activeStep === 2 && (
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" color="gray.600">Recent delivery records</Text>
                <Button alignSelf="flex-start" size="sm" variant="outline" onClick={() => setActiveStep(0)}>New push</Button>
              </VStack>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="sm">Delivery Table</Heading>
              <Button
                ref={retryBtnRef}
                size="sm"
                colorScheme="orange"
                variant="outline"
                isLoading={retrying}
                onClick={handleRetryFailed}
              >
                Retry Failed
              </Button>
            </HStack>
          </CardHeader>
          <CardBody overflowX="auto">
            <Table size="sm">
              <Thead><Tr><Th>ID</Th><Th>Package</Th><Th>Device</Th><Th>Status</Th><Th>Delivered</Th><Th>Ack</Th></Tr></Thead>
              <Tbody>
                {deliveries.map((d) => (
                  <Tr key={d.id}>
                    <Td>{d.id}</Td>
                    <Td>{d.content_package_id}</Td>
                    <Td>{d.device_id}</Td>
                    <Td><Badge colorScheme={d.status === 'delivered' ? 'green' : d.status === 'failed' ? 'red' : 'gray'}>{d.status}</Badge></Td>
                    <Td>{d.delivered_at ?? '—'}</Td>
                    <Td>{d.acknowledged_at ?? '—'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </Box>
    </AdminLayout>
  )
}
